<template>
  <section
    v-if="visible"
    ref="ad"
    :class="['partner-ad', { 'partner-ad--compact': compact }]"
    :aria-label="$t('home.partner_ad.section_label')"
  >
    <a
      class="partner-ad__card"
      :href="config.targetUrl"
      target="_blank"
      rel="sponsored nofollow noopener noreferrer"
      :aria-label="$t('home.partner_ad.title')"
      @click="trackClick"
    >
      <span class="partner-ad__label">{{ $t("home.partner_ad.sponsored") }}</span>

      <picture>
        <source srcset="@/assets/index/img/index/banner_adv_ads.webp" type="image/webp">
        <img
          class="partner-ad__image"
          src="@/assets/index/img/index/banner_adv_ads.jpg"
          width="2560"
          height="640"
          loading="lazy"
          decoding="async"
          :alt="$t('home.partner_ad.image_alt')"
        >
      </picture>

      <span class="partner-ad__content">
        <span class="partner-ad__copy">
          <strong>{{ $t("home.partner_ad.title") }}</strong>
          <span>{{ $t("home.partner_ad.description") }}</span>
        </span>
        <span class="partner-ad__action">{{ $t("home.partner_ad.action") }}</span>
      </span>
    </a>
  </section>
</template>

<script>
import { trackAnalyticsEvent } from '@/util/analytics'

export default {
  name: 'PartnerAdBanner',
  props: {
    config: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      impressionObserver: null,
      impressionTimer: null,
      impressionTracked: false,
    }
  },
  computed: {
    visible() {
      if (!this.config || !this.config.enabled || !this.config.targetUrl) {
        return false
      }

      const locale = String(this.$i18n.locale || '').toLowerCase()
      return this.config.locales.includes(locale)
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        this.clearImpressionTracking()
        if (isVisible) {
          this.$nextTick(this.observeImpression)
        }
      },
    },
  },
  beforeDestroy() {
    this.clearImpressionTracking()
  },
  methods: {
    observeImpression() {
      if (!this.$refs.ad || this.impressionTracked) {
        return
      }

      if (!('IntersectionObserver' in window)) {
        this.trackImpression()
        return
      }

      this.impressionObserver = new IntersectionObserver(entries => {
        const entry = entries[0]
        if (entry && entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!this.impressionTimer) {
            this.impressionTimer = window.setTimeout(this.trackImpression, 1000)
          }
        } else if (this.impressionTimer) {
          window.clearTimeout(this.impressionTimer)
          this.impressionTimer = null
        }
      }, { threshold: [0, 0.5] })

      this.impressionObserver.observe(this.$refs.ad)
    },
    clearImpressionTracking() {
      if (this.impressionObserver) {
        this.impressionObserver.disconnect()
        this.impressionObserver = null
      }
      if (this.impressionTimer) {
        window.clearTimeout(this.impressionTimer)
        this.impressionTimer = null
      }
    },
    trackImpression() {
      if (this.impressionTracked) {
        return
      }
      this.impressionTracked = true
      this.clearImpressionTracking()
      this.track('partner_ad_impression')
    },
    trackClick() {
      this.track('partner_ad_click', {
        target: this.config.targetUrl,
      })
    },
    track(eventName, extra = {}) {
      trackAnalyticsEvent(eventName, {
        partner: this.config.partner,
        placement: this.config.placement,
        locale: this.$i18n.locale,
        ...extra,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.partner-ad {
  width: 100%;
  padding: 32px 20px;
  background: #f0f2f8;
}

.partner-ad__card {
  position: relative;
  display: block;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid rgba(28, 59, 59, 0.12);
  border-radius: 16px;
  background: #fff;
  color: #1c3b3b;
  text-decoration: none;
  box-shadow: 0 12px 32px rgba(28, 59, 59, 0.08);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.partner-ad__card:hover,
.partner-ad__card:focus-visible {
  color: #1c3b3b;
  transform: translateY(-2px);
  box-shadow: 0 18px 42px rgba(28, 59, 59, 0.13);
}

.partner-ad__card:focus-visible {
  outline: 3px solid rgba(255, 138, 61, 0.65);
  outline-offset: 3px;
}

.partner-ad__label {
  position: absolute;
  z-index: 1;
  top: 12px;
  left: 12px;
  padding: 4px 9px;
  border-radius: 999px;
  color: #fff;
  background: rgba(0, 0, 0, 0.68);
  font-size: 12px;
  line-height: 18px;
}

.partner-ad__image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 1;
  object-fit: contain;
  background: #05052d;
}

.partner-ad__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 24px;
}

.partner-ad__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  text-align: left;
}

.partner-ad__copy strong {
  font-size: 18px;
  line-height: 26px;
}

.partner-ad__copy > span {
  margin-top: 3px;
  color: #687a78;
  font-size: 14px;
  line-height: 22px;
}

.partner-ad__action {
  flex: 0 0 auto;
  min-width: 116px;
  padding: 11px 20px;
  border-radius: 999px;
  color: #fff;
  background: #1c3b3b;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

.partner-ad--compact {
  max-width: 1080px;
  padding: 132px 0 0;
  background: transparent;
}

.partner-ad--compact .partner-ad__card {
  border-color: rgba(255, 255, 255, 0.7);
  border-radius: 18px;
  box-shadow: 0 22px 58px rgba(5, 5, 45, 0.18);
}

.partner-ad--compact .partner-ad__content {
  position: absolute;
  right: 18px;
  bottom: 16px;
  gap: 16px;
  padding: 0;
}

.partner-ad--compact .partner-ad__copy {
  display: none;
}

.partner-ad--compact .partner-ad__action {
  min-width: 116px;
  padding: 10px 18px;
  color: #132f2f;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 22px rgba(5, 5, 45, 0.22);
  backdrop-filter: blur(10px);
  font-size: 13px;
}

.partner-ad--compact .partner-ad__label {
  background: rgba(5, 5, 45, 0.72);
  box-shadow: 0 6px 18px rgba(5, 5, 45, 0.2);
  backdrop-filter: blur(8px);
}

@media (max-width: 640px) {
  .partner-ad {
    padding: 20px 16px 28px;
  }

  .partner-ad__card {
    border-radius: 12px;
  }

  .partner-ad__label {
    top: 8px;
    left: 8px;
  }

  .partner-ad__image {
    min-height: 96px;
  }

  .partner-ad__content {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
  }

  .partner-ad__copy strong {
    font-size: 17px;
  }

  .partner-ad__action {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
  }

  .partner-ad--compact {
    padding: 8px 0 0;
  }

  .partner-ad--compact .partner-ad__image {
    min-height: 0;
  }

  .partner-ad--compact .partner-ad__content {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .partner-ad__card {
    transition: none;
  }
}
</style>
